// Shared by both map engines (GoogleMap.tsx, OsmMap.tsx): props, remembered buttons, and the
// marker elements, so vehicles look and behave the same on either map.
import { ReactNode } from 'react';
import { Geofence, Place, TrackPoint } from '../api';
import type { Drawing } from './GeofenceEditor';
import { MapState, shapeSvg, STATE_LABEL } from './VehicleState';

export interface MapVehicle {
  id: number;
  name: string;
  state: MapState;
  lat: number;
  lon: number;
  course: number | null;
  detail?: string;   // shown beside the name when zoomed in, e.g. "48 km/h"
}

export interface MapRoute {
  points: TrackPoint[];
  from: Place;
  to: Place;
  stops?: Place[];
}

export interface Props {
  vehicles?: MapVehicle[];
  selectedId?: number | null;
  follow?: boolean;
  onSelect?: (id: number) => void;
  route?: MapRoute | null;
  stops?: Place[];
  // Journey starts drawn as numbered pins (the day view), matching the numbers in the list.
  numbered?: Place[];
  // Changes to this value re-fit the map to everything shown.
  fitKey?: string | number;
  // False while the page is still loading what the map should show. The map stays
  // hidden and doesn't move until then, so it never flashes Ireland or a half-loaded view.
  ready?: boolean;
  // Card shown on the map above the selected vehicle (7A), and what closing it does.
  popup?: ReactNode;
  onClosePopup?: () => void;
  // Names / Group buttons (the live map). Without them names are always shown and nothing is grouped.
  markerOptions?: boolean;
  // Geofences drawn under everything else; hidden when showGeofences is false (the Geofences
  // card's setting, see geofenceStore).
  geofences?: Geofence[];
  showGeofences?: boolean;
  // Drawing a geofence on this map (the Geofences card; Google only), and a place to move to.
  drawing?: Drawing | null;
  goTo?: { key: number; lat: number; lon: number } | null;
}

// West, south, east, north.
export const IRELAND = { w: -10.7, s: 51.3, e: -5.4, n: 55.5 };

// Map / Satellite (Google only), names and grouping (user request), remembered per browser.
export const SAT_KEY = 'ff.map.satellite';
export const NAMES_KEY = 'ff.map.names';
export const GROUP_KEY = 'ff.map.group';
export const readFlag = (key: string, dflt: boolean) => {
  try { const v = localStorage.getItem(key); return v === null ? dflt : v === '1'; } catch { return dflt; }
};
export const saveFlag = (key: string, on: boolean) => { try { localStorage.setItem(key, on ? '1' : '0'); } catch { /* private mode */ } };

// Markers keep their shape (arrow when moving) at every zoom (user request; dots when far out
// were too small). The detail beside the name ("48 km/h") only shows close in.
export const zoomBand = (z: number) => (z < 12 ? 'out' : 'near');
// Grouping: vehicles closer than this on screen become one count bubble; from GROUP_MAX_ZOOM
// in, nothing is grouped, so vehicles parked side by side can still be picked.
export const GROUP_PX = 40;
export const GROUP_MAX_ZOOM = 16;
export const cssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

// Greedy grouping on screen positions: each point joins the first group within GROUP_PX.
export function groupNear(points: Array<{ id: number; x: number; y: number }>): number[][] {
  const near: Array<{ x: number; y: number; ids: number[] }> = [];
  for (const p of points) {
    const g = near.find((g) => Math.hypot(g.x - p.x, g.y - p.y) < GROUP_PX);
    if (g) g.ids.push(p.id);
    else near.push({ x: p.x, y: p.y, ids: [p.id] });
  }
  return near.filter((g) => g.ids.length > 1).map((g) => g.ids);
}

export function vehicleElement(onPick: () => void): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'veh';
  el.setAttribute('role', 'button');
  el.tabIndex = 0;
  el.innerHTML = '<span class="veh-shape"></span><span class="veh-label"><b></b><small></small></span>';
  el.addEventListener('click', (e) => { e.stopPropagation(); onPick(); });
  el.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(); } });
  return el;
}

export function paintVehicle(el: HTMLElement, v: MapVehicle, selected: boolean) {
  el.dataset.state = v.state;
  el.classList.toggle('selected', selected);
  const shape = el.querySelector<HTMLSpanElement>('.veh-shape')!;
  const key = `${v.state}|${v.state === 'moving' ? Math.round(v.course ?? 0) : ''}`;
  if (shape.dataset.key !== key) { shape.innerHTML = shapeSvg(v.state, v.course); shape.dataset.key = key; }
  el.querySelector('.veh-label b')!.textContent = v.name;
  el.querySelector('.veh-label small')!.textContent = v.detail ?? '';
  el.setAttribute('aria-label', `${v.name}, ${STATE_LABEL[v.state]}${v.detail ? `, ${v.detail}` : ''}`);
  el.title = `${v.name} · ${STATE_LABEL[v.state]}`;
}

// The count bubble for a group; picking it zooms in on the group's vehicles.
export function groupElement(onPick: (ids: number[]) => void): HTMLDivElement {
  const el = dot('veh-group');
  el.setAttribute('role', 'button');
  el.tabIndex = 0;
  const open = () => onPick((el.dataset.ids ?? '').split(',').map(Number));
  el.addEventListener('click', (e) => { e.stopPropagation(); open(); });
  el.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
  return el;
}

export function paintGroup(el: HTMLElement, ids: number[], names: string) {
  el.textContent = String(ids.length);
  el.dataset.ids = ids.join(',');
  el.title = names;
  el.setAttribute('aria-label', `${ids.length} vehicles: ${names}. Zoom in`);
}

export function dot(className: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = className;
  return el;
}

// Stops wear the parked shape, as on the live map.
export function stopElement(): HTMLDivElement {
  const el = dot('pin-stop st-parked');
  el.innerHTML = shapeSvg('parked');
  return el;
}

export function numberElement(n: number): HTMLDivElement {
  const el = dot('pin-num');
  el.textContent = String(n);
  return el;
}

// A small switch button for the map's corner (Map / Satellite, Names, Group).
export function flagButton(label: string, title: string, pressed: boolean, onToggle: (on: boolean) => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'map-layer-btn';
  b.textContent = label;
  b.title = title;
  b.setAttribute('aria-pressed', String(pressed));
  b.onclick = () => {
    const on = b.getAttribute('aria-pressed') !== 'true';
    b.setAttribute('aria-pressed', String(on));
    onToggle(on);
  };
  return b;
}

// Everything the map should show, as [lon, lat], for fitting the view.
export function fitCoords({ vehicles = [], route = null, stops = [], numbered = [], geofences = [] }: Props): Array<[number, number]> {
  const coords: Array<[number, number]> = [];
  if (route) route.points.forEach((p) => coords.push([p[2], p[1]]));
  stops.forEach((s) => coords.push([s.lon, s.lat]));
  numbered.forEach((s) => coords.push([s.lon, s.lat]));
  if (!route && stops.length === 0) vehicles.forEach((v) => coords.push([v.lon, v.lat]));
  // A map of geofences only (the Geofences page) fits them.
  if (coords.length === 0) geofences.forEach((g) => g.points.forEach((p) => coords.push(p)));
  return coords;
}

// Geofences: outline in the geofence's colour, a light fill of the same, and its name in the
// middle (only when zoomed in, like vehicle details).
export const geofenceColour = (g: Pick<Geofence, 'colour'>) => cssVar(`--geofence-${g.colour}`) || '#2a78d6';
export const GEOFENCE_FILL_OPACITY = 0.12;

export function geofenceCentre(g: Pick<Geofence, 'circle' | 'points'>): { lat: number; lon: number } {
  if (g.circle) return { lat: g.circle.lat, lon: g.circle.lon };
  const n = g.points.length;
  return { lat: g.points.reduce((a, p) => a + p[1], 0) / n, lon: g.points.reduce((a, p) => a + p[0], 0) / n };
}

export function geofenceLabel(g: Pick<Geofence, 'name' | 'colour'>): HTMLDivElement {
  const el = dot('geo-label');
  const name = document.createElement('span');
  name.textContent = g.name;
  el.append(name);
  el.style.setProperty('--geo', `var(--geofence-${g.colour})`);
  return el;
}
